import { Calendar, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/formatDate';
import { isDeadlineUrgent } from '@/utils/isDeadlineUrgent';
import { isInquiryOfferDeadlinePassed } from '@/utils/isInquiryOfferDeadlinePassed';

type InquiryOfferDeadlineDisplayProps = {
  deadline?: string | null;
  showLabel?: boolean;
  labelKey?: string;
  showIcon?: boolean;
  locale?: string;
  className?: string;
};

export function InquiryOfferDeadlineDisplay({
  deadline,
  showLabel = false,
  labelKey = 'projects.form.timelineBudget.deadline.label',
  showIcon = true,
  locale,
  className,
}: InquiryOfferDeadlineDisplayProps) {
  const { t } = useTranslation();

  if (!deadline) {
    return (
      <span className={cn('text-muted-foreground', className)}>
        {t('projects.form.review.notSpecified')}
      </span>
    );
  }

  const passed = isInquiryOfferDeadlinePassed(deadline);
  const urgent = !passed && isDeadlineUrgent(deadline);

  return (
    <div
      className={cn('flex items-center gap-1', className)}
      title={passed ? t('offers.form.messages.deadlinePassed') : undefined}
    >
      {showIcon && (
        <Calendar
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground',
            passed && 'text-destructive',
            urgent && 'text-orange-600 dark:text-orange-500',
          )}
        />
      )}
      {showLabel && (
        <span className="text-muted-foreground">{t(labelKey)}:</span>
      )}
      <span
        className={cn(
          'text-muted-foreground',
          passed && 'font-medium text-destructive',
          urgent && 'font-medium text-orange-600 dark:text-orange-500',
        )}
      >
        {formatDate(deadline, locale)}
      </span>
      {urgent && <Clock className="h-3.5 w-3.5 shrink-0 text-orange-600 dark:text-orange-500" />}
    </div>
  );
}
